import { MarketplaceDetail } from '@anytag/graphql';
import { formatIntNumber } from '@anytag/shared-utils';
import { useTranslateOptions, useAllInfluencerCategoryOptions } from '@anytag/shared-hooks';
import {
  MAX_AGE_V2,
  MIN_AGE_V2,
  MAX_FOLLOWERS,
  MIN_FOLLOWERS,
  GENDER_OPTIONS,
  MIN_ENGAGEMENT_RATE,
  MAX_ENGAGEMENT_RATE,
  MIN_MAX_AGE_OPTIONS_V2,
  MIN_MAX_EN_RATE_OPTIONS
} from '@anytag/shared-constants';
import { RangeSliderInputFieldProps } from '@anytag/shared-molecules';

interface Props {
  marketplace: MarketplaceDetail;
}

export const useMarketplaceInfluencersFilterOptions = ({
  marketplace: { minAge, maxAge, minFollowers, maxFollowers, genders, categories }
}: Props) => {
  const allGenderOptions = useTranslateOptions(GENDER_OPTIONS);
  const { allInfluencerCategoryOptions } = useAllInfluencerCategoryOptions();

  const followerAmountOptions = 10;
  const customMinAge = minAge ?? MIN_AGE_V2;
  const customMaxAge = maxAge || MAX_AGE_V2;
  const customMinFollowers = minFollowers || MIN_FOLLOWERS;
  const customMaxFollowers = maxFollowers || MAX_FOLLOWERS;
  const followerStep = Math.floor((customMaxFollowers - customMinFollowers) / followerAmountOptions);
  const followerOptions = Array.from({ length: followerAmountOptions + 1 }, (_, i) => {
    const value = i * followerStep;

    return {
      value: String(value),
      label: `${formatIntNumber(value)}${value === customMaxFollowers ? '+' : ''}`
    };
  });
  const categoryIds = categories.map(({ id }) => String(id));
  const genderOptions = allGenderOptions.map((option) => ({
    ...option,
    disabled: !genders.includes(option.value)
  }));
  const influencerCategoryOptions = allInfluencerCategoryOptions.map((option) => ({
    ...option,
    disabled: !categoryIds.includes(option.value)
  }));

  return {
    genderOptions,
    influencerCategoryOptions,
    ageConfigs: {
      options: MIN_MAX_AGE_OPTIONS_V2,
      slideProps: { step: 1, min: customMinAge, max: customMaxAge }
    } satisfies Pick<RangeSliderInputFieldProps<string>, 'options' | 'slideProps'>,
    followerConfigs: {
      options: followerOptions,
      slideProps: { step: followerStep, min: customMinFollowers, max: customMaxFollowers }
    } satisfies Pick<RangeSliderInputFieldProps<string>, 'options' | 'slideProps'>,
    engagementRateConfigs: {
      options: MIN_MAX_EN_RATE_OPTIONS,
      slideProps: { step: 0.1, min: MIN_ENGAGEMENT_RATE, max: MAX_ENGAGEMENT_RATE }
    } satisfies Pick<RangeSliderInputFieldProps<string>, 'options' | 'slideProps'>
  };
};
